import { CheckOutlined } from '@mui/icons-material'
import { Alert, Snackbar, Typography } from '@mui/material'
import type { ReactNode } from 'react'
import React, { useEffect, useState } from 'react'

import type { SnackbarProps } from './SnackbarContext'
import SnackbarContext from './SnackbarContext'

type SnackbarProviderProps = {
  children: ReactNode
}

const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }: SnackbarProviderProps) => {
  const [snackbarStack, setSnackbarStack] = useState<SnackbarProps[]>([])
  const [activeSnackbar, setActiveSnackbar] = useState<SnackbarProps>()
  const [open, setOpen] = useState(false)

  const showSnackbar = (snackbarOptions: SnackbarProps): void => {
    setSnackbarStack(prev => [...prev, snackbarOptions])
  }

  const handleClose = (): void => {
    setOpen(false)
  }

  useEffect(() => {
    if (!open && snackbarStack.length) {
      // Set a new snack when we don't have an active one
      setActiveSnackbar({ ...snackbarStack[0] })
      setSnackbarStack(prev => prev.slice(1))
      setOpen(true)
    }
  }, [snackbarStack, open])

  return (
    <SnackbarContext.Provider value={{ showSnackbar, closeSnackbar: handleClose }}>
      <Snackbar
        sx={{ top: 8 }}
        key={activeSnackbar?.message?.toString()}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        open={open}
        autoHideDuration={activeSnackbar?.disableAutoClose ? null : 10000}
        onClose={handleClose}
        onAnimationEnd={(): void => setActiveSnackbar(undefined)}
      >
        <Alert
          onClose={handleClose}
          severity={activeSnackbar?.severity}
          elevation={2}
          variant="filled"
          iconMapping={{
            success: <CheckOutlined fontSize="inherit" />,
          }}
          sx={{
            alignItems: 'flex-start',
            paddingTop: '16px',
            '& .MuiAlert-icon': {
              paddingTop: '0px',
            },
            '& .MuiAlert-action': {
              marginTop: '-4px',
              marginRight: '4px',
              marginLeft: '12px',
              paddingTop: 0,
            },
          }}
        >
          {activeSnackbar?.message && <Typography variant="body1">{activeSnackbar.message}</Typography>}
        </Alert>
      </Snackbar>
      {children}
    </SnackbarContext.Provider>
  )
}

export default SnackbarProvider
